'use client';

import React, { useState, useEffect, useCallback } from 'react';
import { Search, ChevronUp, ChevronDown, Eye, CheckCircle, XCircle, AlertCircle, Loader2, ChevronDown as ExpandIcon } from 'lucide-react';
import Badge from '@/components/ui/Badge';
import { toast } from 'sonner';
import { timesheetsService, type Timesheet, type ApprovalStatus } from '@/lib/services/staffingService';

type SortKey = keyof Timesheet;

export default function ShiftLogTable() {
  const [shiftLogs, setShiftLogs] = useState<Timesheet[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('clockIn');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());
  const [currentPage, setCurrentPage] = useState(1);
  const [expandedCards, setExpandedCards] = useState<Set<string>>(new Set());
  const rowsPerPage = 8;

  const loadTimesheets = useCallback(async () => {
    try {
      const data = await timesheetsService.getAll();
      setShiftLogs(data);
    } catch {
      toast.error('Failed to load timesheets');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTimesheets();
    const unsub = timesheetsService.subscribe(
      (t) => setShiftLogs((prev) => [...prev, t]),
      (t) => setShiftLogs((prev) => prev.map((x) => (x.id === t.id ? t : x))),
      (id) => setShiftLogs((prev) => prev.filter((x) => x.id !== id)),
    );
    return unsub;
  }, [loadTimesheets]);

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const filtered = shiftLogs.filter((row) => {
    const matchSearch =
      row.staffName.toLowerCase().includes(search.toLowerCase()) ||
      row.facility.toLowerCase().includes(search.toLowerCase()) ||
      row.specialty.toLowerCase().includes(search.toLowerCase());
    const matchStatus = filterStatus === 'all' || row.approvalStatus === filterStatus;
    return matchSearch && matchStatus;
  });

  const sorted = [...filtered].sort((a, b) => {
    const av = a[sortKey];
    const bv = b[sortKey];
    if (typeof av === 'number' && typeof bv === 'number') {
      return sortDir === 'asc' ? av - bv : bv - av;
    }
    return sortDir === 'asc'
      ? String(av).localeCompare(String(bv))
      : String(bv).localeCompare(String(av));
  });

  const totalPages = Math.ceil(sorted.length / rowsPerPage);
  const paginated = sorted.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage);

  const toggleRow = (id: string) => {
    const next = new Set(selectedRows);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelectedRows(next);
  };

  const toggleAll = () => {
    if (selectedRows.size === paginated.length) {
      setSelectedRows(new Set());
    } else {
      setSelectedRows(new Set(paginated.map((r) => r.id)));
    }
  };

  const toggleCard = (id: string) => {
    const next = new Set(expandedCards);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setExpandedCards(next);
  };

  const handleBulkApprove = async () => {
    const ids = Array.from(selectedRows);
    try {
      await timesheetsService.bulkUpdateStatus(ids, 'approved' as ApprovalStatus);
      setShiftLogs((prev) =>
        prev.map((r) => (selectedRows.has(r.id) ? { ...r, approvalStatus: 'approved' as ApprovalStatus } : r))
      );
      setSelectedRows(new Set());
      toast.success(`${ids.length} timesheet(s) approved`);
    } catch {
      toast.error('Failed to approve timesheets');
    }
  };

  const SortIcon = ({ col }: { col: SortKey }) => (
    <span className="inline-flex flex-col ml-1 opacity-40">
      <ChevronUp size={9} className={sortKey === col && sortDir === 'asc' ? 'opacity-100 text-primary' : ''} />
      <ChevronDown size={9} className={sortKey === col && sortDir === 'desc' ? 'opacity-100 text-primary' : ''} />
    </span>
  );

  const approvalIcon = (status: string) => {
    if (status === 'approved') return <CheckCircle size={13} className="text-accent" />;
    if (status === 'rejected') return <XCircle size={13} className="text-danger" />;
    return <AlertCircle size={13} className="text-warning" />;
  };

  if (loading) {
    return (
      <div className="card-elevated flex items-center justify-center py-16">
        <Loader2 size={24} className="animate-spin text-primary" />
        <span className="ml-3 text-muted-foreground text-sm">Loading timesheets…</span>
      </div>
    );
  }

  const PaginationBar = () =>
    totalPages > 1 ? (
      <div className="flex items-center justify-between px-4 py-3 border-t border-border">
        <p className="text-xs text-muted-foreground">
          Showing {(currentPage - 1) * rowsPerPage + 1}–{Math.min(currentPage * rowsPerPage, sorted.length)} of {sorted.length}
        </p>
        <div className="flex items-center gap-1">
          <button
            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            className="px-2.5 py-1 text-xs rounded-md border border-border hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            Prev
          </button>
          {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
            <button
              key={p}
              onClick={() => setCurrentPage(p)}
              className={`px-2.5 py-1 text-xs rounded-md border transition-colors ${p === currentPage ? 'border-primary bg-primary text-primary-foreground' : 'border-border hover:bg-muted'}`}
            >
              {p}
            </button>
          ))}
          <button
            onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
            className="px-2.5 py-1 text-xs rounded-md border border-border hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            Next
          </button>
        </div>
      </div>
    ) : null;

  return (
    <div className="card-elevated overflow-hidden">
      {/* Table header controls */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 border-b border-border">
        <h3 className="text-[15px] font-600 text-foreground">Shift Log</h3>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search staff, facility..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }}
              className="pl-7 pr-3 py-1.5 text-sm bg-muted rounded-lg border border-transparent focus:border-primary focus:bg-card focus:outline-none w-48 transition-all"
            />
          </div>
          <select
            value={filterStatus}
            onChange={(e) => { setFilterStatus(e.target.value); setCurrentPage(1); }}
            className="pl-3 pr-7 py-1.5 text-sm bg-muted rounded-lg border border-transparent focus:border-primary focus:outline-none appearance-none cursor-pointer"
          >
            <option value="all">All Status</option>
            <option value="approved">Approved</option>
            <option value="pending">Pending</option>
            <option value="rejected">Rejected</option>
          </select>
        </div>
      </div>

      {/* Bulk action bar */}
      {selectedRows.size > 0 && (
        <div className="flex items-center gap-3 px-4 py-2.5 bg-primary/5 border-b border-primary/10 slide-up">
          <span className="text-sm font-500 text-primary">{selectedRows.size} selected</span>
          <button
            onClick={handleBulkApprove}
            className="text-xs px-3 py-1 rounded-md bg-accent text-white font-500 hover:bg-accent/90 transition-colors"
          >
            Approve All
          </button>
          <button className="text-xs px-3 py-1 rounded-md bg-muted text-foreground font-500 hover:bg-border transition-colors">
            Export Selected
          </button>
          <button
            onClick={() => setSelectedRows(new Set())}
            className="text-xs text-muted-foreground hover:text-foreground ml-auto"
          >
            Clear
          </button>
        </div>
      )}

      {/* ── MOBILE CARD STACK (below md) ── */}
      <div className="md:hidden">
        {paginated.length === 0 && (
          <p className="text-center py-12 text-muted-foreground text-sm">No timesheets found.</p>
        )}
        <div className="divide-y divide-border">
          {paginated.map((row) => {
            const isExpanded = expandedCards.has(row.id);
            return (
              <div
                key={row.id}
                className={`px-4 py-3 transition-colors ${selectedRows.has(row.id) ? 'bg-primary/5' : ''} ${row.missedPunch ? 'bg-danger/5' : ''}`}
              >
                {/* Card header row — always visible */}
                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    checked={selectedRows.has(row.id)}
                    onChange={() => toggleRow(row.id)}
                    className="rounded border-border mt-0.5 flex-shrink-0"
                  />
                  {/* Avatar */}
                  <div className="w-8 h-8 rounded-full bg-primary/10 text-primary text-[11px] font-700 flex items-center justify-center flex-shrink-0">
                    {row.staffName.split(' ').map((n) => n[0]).join('')}
                  </div>
                  {/* Name + role + status */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-[13px] font-600 text-foreground truncate">{row.staffName}</p>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        {approvalIcon(row.approvalStatus)}
                        <Badge variant={row.approvalStatus}>{row.approvalStatus}</Badge>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                      <Badge variant={row.role}>{row.role.toUpperCase()}</Badge>
                      <span className="text-[11px] text-muted-foreground">{row.specialty}</span>
                    </div>
                    {row.missedPunch && (
                      <p className="text-[10px] text-danger font-500 mt-0.5">⚠ Missed punch</p>
                    )}
                  </div>
                  {/* Expand toggle */}
                  <button
                    onClick={() => toggleCard(row.id)}
                    className="p-1 rounded-md hover:bg-muted text-muted-foreground transition-colors flex-shrink-0"
                    aria-label={isExpanded ? 'Collapse details' : 'Expand details'}
                  >
                    <ExpandIcon
                      size={15}
                      className={`transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
                    />
                  </button>
                </div>

                {/* Quick summary row — always visible */}
                <div className="mt-2 ml-11 grid grid-cols-3 gap-x-4 gap-y-1">
                  <div>
                    <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Clock In</p>
                    <p className="text-[12px] font-mono-data text-foreground">{row.clockIn}</p>
                  </div>
                  <div>
                    <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Clock Out</p>
                    <p className="text-[12px] font-mono-data text-foreground">{row.clockOut}</p>
                  </div>
                  <div>
                    <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Reg. Hrs</p>
                    <p className="text-[12px] font-mono-data font-600 text-foreground">{row.regularHours}h</p>
                  </div>
                </div>

                {/* Collapsible detail rows */}
                {isExpanded && (
                  <div className="mt-3 ml-11 grid grid-cols-2 gap-x-4 gap-y-2 border-t border-border pt-3">
                    <div>
                      <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Facility</p>
                      <p className="text-[12px] text-foreground">{row.facility}</p>
                    </div>
                    <div>
                      <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Break</p>
                      <p className="text-[12px] text-muted-foreground">{row.breakMin}m</p>
                    </div>
                    <div>
                      <p className="text-[10px] text-muted-foreground uppercase tracking-wide">OT Hours</p>
                      <p className={`text-[12px] font-mono-data font-600 ${row.otHours > 0 ? 'text-warning' : 'text-muted-foreground'}`}>
                        {row.otHours > 0 ? `+${row.otHours}h` : '—'}
                      </p>
                    </div>
                    <div className="flex items-end">
                      <button className="p-1.5 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground transition-colors" title="View shift details">
                        <Eye size={14} />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <PaginationBar />
      </div>

      {/* ── DESKTOP TABLE (md and above) ── */}
      <div className="hidden md:block">
        <div className="overflow-x-auto scrollbar-thin">
          <table className="w-full min-w-[900px]">
            <thead>
              <tr className="bg-muted/50 border-b border-border">
                <th className="w-10 px-4 py-3">
                  <input
                    type="checkbox"
                    checked={selectedRows.size === paginated.length && paginated.length > 0}
                    onChange={toggleAll}
                    className="rounded border-border"
                  />
                </th>
                {[
                  { key: 'staffName' as SortKey, label: 'Staff Member' },
                  { key: 'role' as SortKey, label: 'Role' },
                  { key: 'specialty' as SortKey, label: 'Specialty' },
                  { key: 'facility' as SortKey, label: 'Facility' },
                  { key: 'clockIn' as SortKey, label: 'Clock In' },
                  { key: 'clockOut' as SortKey, label: 'Clock Out' },
                  { key: 'breakMin' as SortKey, label: 'Break' },
                  { key: 'regularHours' as SortKey, label: 'Reg. Hrs' },
                  { key: 'otHours' as SortKey, label: 'OT Hrs' },
                  { key: 'approvalStatus' as SortKey, label: 'Status' },
                ].map((col) => (
                  <th
                    key={`th-${col.key}`}
                    onClick={() => handleSort(col.key)}
                    className="px-4 py-3 text-left text-[11px] font-600 text-muted-foreground uppercase tracking-wide cursor-pointer hover:text-foreground select-none whitespace-nowrap"
                  >
                    {col.label}
                    <SortIcon col={col.key} />
                  </th>
                ))}
                <th className="px-4 py-3 text-left text-[11px] font-600 text-muted-foreground uppercase tracking-wide">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {paginated.length === 0 && (
                <tr>
                  <td colSpan={12} className="text-center py-12 text-muted-foreground text-sm">No timesheets found.</td>
                </tr>
              )}
              {paginated.map((row) => (
                <tr
                  key={row.id}
                  className={`row-hover transition-colors ${selectedRows.has(row.id) ? 'bg-primary/5' : ''} ${row.missedPunch ? 'bg-danger/5' : ''}`}
                >
                  <td className="px-4 py-3">
                    <input
                      type="checkbox"
                      checked={selectedRows.has(row.id)}
                      onChange={() => toggleRow(row.id)}
                      className="rounded border-border"
                    />
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 rounded-full bg-primary/10 text-primary text-[11px] font-700 flex items-center justify-center flex-shrink-0">
                        {row.staffName.split(' ').map((n) => n[0]).join('')}
                      </div>
                      <div>
                        <p className="text-[13px] font-500 text-foreground">{row.staffName}</p>
                        {row.missedPunch && (
                          <p className="text-[10px] text-danger font-500">⚠ Missed punch</p>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <Badge variant={row.role}>{row.role.toUpperCase()}</Badge>
                  </td>
                  <td className="px-4 py-3 text-[13px] text-muted-foreground">{row.specialty}</td>
                  <td className="px-4 py-3 text-[13px] text-foreground max-w-[130px] truncate">{row.facility}</td>
                  <td className="px-4 py-3 text-[13px] font-mono-data text-foreground">{row.clockIn}</td>
                  <td className="px-4 py-3 text-[13px] font-mono-data text-foreground">{row.clockOut}</td>
                  <td className="px-4 py-3 text-[13px] text-muted-foreground">{row.breakMin}m</td>
                  <td className="px-4 py-3 text-[13px] font-mono-data font-500 text-foreground">{row.regularHours}h</td>
                  <td className="px-4 py-3">
                    <span className={`text-[13px] font-mono-data font-600 ${row.otHours > 0 ? 'text-warning' : 'text-muted-foreground'}`}>
                      {row.otHours > 0 ? `+${row.otHours}h` : '—'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1.5">
                      {approvalIcon(row.approvalStatus)}
                      <Badge variant={row.approvalStatus}>{row.approvalStatus}</Badge>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <button className="p-1.5 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground transition-colors" title="View shift details">
                      <Eye size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <PaginationBar />
      </div>
    </div>
  );
}